// @ts-nocheck
import { morphSection } from "@theme/section-renderer";
import { debounce, fetchConfig } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";

const REWARD_GIFT_PROP = "_reward_gift";

class CartRewardsComponent extends HTMLElement {
  #isSubmittingCheckout = false;
  #syncTimer = null;
  #syncInFlight = false;
  #lastDiscountState = "";
  #lastGiftState = "";
  #giftSyncInFlight = false;
  #onCartUpdateDebounced = null;
  #milestoneResizeObserver = null;
  #milestoneLayoutRetries = 0;
  #checkoutClickHandler = null;
  #checkoutEventTarget = null;
  static get observedAttributes() {
    return [
      "data-rewards-config",
      "data-currency",
      "data-cart-variant-ids",
      "data-accent-color",
      "data-congratz-color",
    ];
  }

  connectedCallback() {
    this.#applyThemeColors();
    this.#render();
    this.#scheduleRewardStateSync();
    this.#bindCheckoutGiftHandler();
    this.#onCartUpdateDebounced = debounce(() => {
      void this.#syncFreeGiftLineItems();
    }, 650);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateDebounced);
  }

  disconnectedCallback() {
    this.#milestoneResizeObserver?.disconnect();
    this.#milestoneResizeObserver = null;
    if (this.#onCartUpdateDebounced) {
      document.removeEventListener(
        ThemeEvents.cartUpdate,
        this.#onCartUpdateDebounced
      );
      this.#onCartUpdateDebounced = null;
    }
    if (this.#checkoutClickHandler && this.#checkoutEventTarget) {
      this.#checkoutEventTarget.removeEventListener(
        "click",
        this.#checkoutClickHandler,
        true
      );
      this.#checkoutClickHandler = null;
      this.#checkoutEventTarget = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;
    if (name === "data-accent-color" || name === "data-congratz-color") {
      this.#applyThemeColors();
      this.#render();
      this.#scheduleRewardStateSync();
      return;
    }
    if (name === "data-rewards-config" || name === "data-currency") {
      this.#render();
      this.#scheduleRewardStateSync();
    }
  }

  #getConfig() {
    try {
      return JSON.parse(this.dataset.rewardsConfig || "{}");
    } catch (error) {
      return null;
    }
  }

  #money(cents) {
    const amount = Number(cents || 0) / 100;
    const currency =
      this.dataset.currency || window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }

  #thresholdValue(cents) {
    const amount = Math.round(Number(cents || 0) / 100);
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 0,
    }).format(amount);
  }

  #resolveColor(value, fallback) {
    const color = String(value || "").trim();
    return color || fallback;
  }

  #applyThemeColors() {
    const accent = this.#resolveColor(this.dataset.accentColor, "#F4E5B1");
    const congratz = this.#resolveColor(this.dataset.congratzColor, "#1E5A35");
    this.style.setProperty("--cart-reward-accent-color", accent);
    this.style.setProperty("--cart-reward-congratz-color", congratz);
  }

  #icon(iconSource, size = 16) {
    const source = (iconSource || "").trim();
    if (!source) return "";
    if (source.includes("/") || source.startsWith("http")) {
      return `
      <span class="cart-rewards__icon" style="width:${size}px;height:${size}px;">
        <img src="${source}" alt="" loading="lazy" width="${size}" height="${size}">
      </span>
    `;
    }
    return `
      <span class="cart-rewards__icon" style="width:${size}px;height:${size}px;">
        <svg aria-hidden="true" focusable="false">
          <use href="/sprites/icons.svg#${source}"></use>
        </svg>
      </span>
    `;
  }

  #buildMilestones(enabledRewards, cartTotalCents) {
    const maxThreshold = Math.max(
      ...enabledRewards.map((reward) => reward.thresholdCents),
      1
    );
    const progressPercent = Math.max(
      0,
      Math.min(100, (cartTotalCents / maxThreshold) * 100)
    );

    const milestones = enabledRewards
      .map((reward) => {
        const positionPercent = Math.max(
          0,
          Math.min(100, (reward.thresholdCents / maxThreshold) * 100)
        );
        const achieved = cartTotalCents >= reward.thresholdCents;
        return { ...reward, positionPercent, achieved };
      })
      .sort((a, b) => a.thresholdCents - b.thresholdCents);

    return { milestones, progressPercent };
  }

  #buildMessage(milestones, cartTotalCents) {
    const nextReward = milestones.find((milestone) => !milestone.achieved);
    const achievedRewards = milestones.filter(
      (milestone) => milestone.achieved
    );
    const rewardProgressText = (reward, remainingText) => {
      if (reward.type === "shipping") {
        return `Spend another ${remainingText} for FREE delivery`;
      }
      if (reward.type === "discount") {
        return `Get ${reward.label} off when you spend ${remainingText} more`;
      }
      return `Spend ${remainingText} more to unlock ${reward.label}`;
    };

    if (achievedRewards.length > 0 && !nextReward) {
      return {
        rows: achievedRewards.map((reward) => ({
          icon: reward.congratzIcon || reward.icon,
          text: reward.achievedText,
          achieved: true,
        })),
      };
    }

    if (achievedRewards.length > 0 && nextReward) {
      const remaining = nextReward.thresholdCents - cartTotalCents;
      const remainingText = this.#money(Math.max(0, remaining));
      return {
        rows: [
          ...achievedRewards.map((reward) => ({
            icon: reward.congratzIcon || reward.icon,
            text: reward.achievedText,
            achieved: true,
          })),
          {
            icon: nextReward.icon,
            text: rewardProgressText(nextReward, remainingText),
            achieved: false,
          },
        ],
      };
    }

    if (nextReward) {
      const remaining = nextReward.thresholdCents - cartTotalCents;
      const remainingText = this.#money(Math.max(0, remaining));
      return {
        rows: [
          {
            icon: nextReward.icon,
            text: rewardProgressText(nextReward, remainingText),
            achieved: false,
          },
        ],
      };
    }

    return null;
  }

  #render() {
    const config = this.#getConfig();
    if (!config) return;

    const cartTotalCents = Number(config.cartTotalCents || 0);
    const barHeight = Number(config.barHeight || 8);
    const rewards = Array.isArray(config.rewards) ? config.rewards : [];
    const enabledRewards = rewards
      .filter((reward) => reward.enabled && Number(reward.thresholdCents) > 0)
      .sort((a, b) => a.thresholdCents - b.thresholdCents);

    if (!enabledRewards.length) {
      this.innerHTML = "";
      return;
    }

    const { milestones, progressPercent } = this.#buildMilestones(
      enabledRewards,
      cartTotalCents
    );
    const hasAchievedReward = milestones.some(
      (milestone) => milestone.achieved
    );
    const message = this.#buildMessage(milestones, cartTotalCents);

    if (!message) {
      this.innerHTML = "";
      return;
    }

    this.dataset.activeDiscountCode = this.#computeRewardDiscountCode(
      config,
      cartTotalCents
    );

    const unlockedGiftVariantIds = milestones
      .filter((milestone) => milestone.achieved && milestone.type === "gift")
      .flatMap((milestone) => milestone.giftVariantIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    this.dataset.unlockedGiftVariantIds = JSON.stringify([
      ...new Set(unlockedGiftVariantIds),
    ]);

    const accentColor = this.#resolveColor(
      this.dataset.accentColor || config.accentColor,
      "#F4E5B1"
    );
    const congratzColor = this.#resolveColor(
      this.dataset.congratzColor || config.congratzColor,
      "#1E5A35"
    );
    const gradientColor = hasAchievedReward ? congratzColor : accentColor;

    this.style.setProperty("--cart-reward-accent-color", accentColor);
    this.style.setProperty("--cart-reward-congratz-color", congratzColor);
    this.style.setProperty("--cart-reward-bar-height", `${barHeight}px`);

    this.innerHTML = `
      <div class="cart-rewards__message">
        ${(message.rows || [])
          .map(
            (row) => `
            <div class="cart-rewards__message-row ${
              row.achieved ? "is-achieved" : "is-default"
            }">
              ${this.#icon(row.icon, 24)}
              <span class="cart-rewards__message-content">
                <span class="${
                  row.achieved
                    ? "cart-rewards__message-text"
                    : "cart-rewards__message-next"
                }">${row.text}</span>
              </span>
            </div>
          `
          )
          .join("")}
      </div>
      <div class="cart-rewards__milestones-wrapper">
        <ol class="cart-rewards__milestones" role="list">
          ${milestones
            .map(
              (milestone) => `
              <li
                class="cart-rewards__milestone ${
                  milestone.achieved ? "is-achieved" : ""
                }"
                style="left:${milestone.positionPercent}%;"
                data-position-percent="${milestone.positionPercent}"
              >
                <div class="cart-rewards__milestone-label">
                  ${this.#icon(milestone.icon, 16)}
                  <p>${milestone.label}</p>
                </div>
                <div class="cart-rewards__milestone-value">${this.#thresholdValue(
                  milestone.thresholdCents
                )}</div>
              </li>
            `
            )
            .join("")}
          <li class="cart-rewards__progress-track" aria-hidden="true">
            <span
              class="cart-rewards__progress-fill"
              style="width:${progressPercent}%; --cart-reward-fill-color:${gradientColor};"
            ></span>
          </li>
        </ol>
      </div>
    `;

    this.#observeMilestones();
    this.#scheduleMilestoneLayout();
  }

  #observeMilestones() {
    this.#milestoneResizeObserver?.disconnect();
    this.#milestoneResizeObserver = null;
    const container = this.querySelector(".cart-rewards__milestones");
    if (!(container instanceof HTMLElement)) return;
    this.#milestoneResizeObserver = new ResizeObserver(() => {
      this.#resolveMilestoneOverlaps();
    });
    this.#milestoneResizeObserver.observe(container);
  }

  #scheduleMilestoneLayout() {
    this.#milestoneLayoutRetries = 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#resolveMilestoneOverlaps();
      });
    });
    for (const delay of [0, 50, 150, 400]) {
      window.setTimeout(() => {
        this.#resolveMilestoneOverlaps();
      }, delay);
    }
  }

  #resolveMilestoneOverlaps() {
    const container = this.querySelector(".cart-rewards__milestones");
    if (!(container instanceof HTMLElement)) return;
    const milestones = [
      ...container.querySelectorAll(".cart-rewards__milestone"),
    ].filter((node) => node instanceof HTMLElement);
    if (milestones.length <= 1) return;

    const width = container.getBoundingClientRect().width;
    if (!width && this.#milestoneLayoutRetries < 12) {
      this.#milestoneLayoutRetries += 1;
      window.setTimeout(() => this.#resolveMilestoneOverlaps(), 32);
      return;
    }
    if (!width) {
      return;
    }

    const gap = 5;
    const items = milestones.map((element) => {
      const positionPercent = Number(element.dataset.positionPercent || 0);
      const itemWidth = Math.max(element.offsetWidth, 40);
      const center = (positionPercent / 100) * width;
      return { element, itemWidth, center };
    });

    const maxIndex = items.length - 1;
    items[maxIndex].center = Math.max(
      items[maxIndex].itemWidth / 2,
      width - items[maxIndex].itemWidth / 2
    );
    for (let i = 0; i < items.length; i += 1) {
      const minCenter = items[i].itemWidth / 2;
      const maxCenter = width - items[i].itemWidth / 2;
      items[i].center = Math.max(
        minCenter,
        Math.min(maxCenter, items[i].center)
      );
    }

    for (let i = 1; i < items.length; i += 1) {
      const previous = items[i - 1];
      const current = items[i];
      const minCenter =
        previous.center + previous.itemWidth / 2 + gap + current.itemWidth / 2;
      if (current.center < minCenter) {
        current.center = minCenter;
      }
    }

    const overflow =
      items[maxIndex].center + items[maxIndex].itemWidth / 2 - width;
    if (overflow > 0) {
      for (let i = maxIndex; i >= 0; i -= 1) {
        items[i].center -= overflow;
      }
    }

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const minCenter = item.itemWidth / 2;
      if (item.center < minCenter) {
        const shift = minCenter - item.center;
        for (let j = i; j < items.length; j += 1) {
          items[j].center += shift;
        }
      }
    }

    items.forEach((item) => {
      item.element.style.left = `${item.center}px`;
      item.element.style.transform = "translateX(-50%)";
    });
  }

  #existingDiscountCodes() {
    const pills = document.querySelectorAll(".cart-discount__pill");
    return [...pills]
      .map((pill) =>
        pill instanceof HTMLLIElement ? pill.dataset.discountCode : null
      )
      .filter(Boolean);
  }

  #scheduleRewardStateSync() {
    if (this.#syncTimer) {
      clearTimeout(this.#syncTimer);
    }
    this.#syncTimer = setTimeout(() => {
      this.#syncTimer = null;
      void this.#syncDiscountAndGifts();
    }, 500);
  }

  async #syncDiscountAndGifts() {
    await this.#syncDiscountReward();
    await this.#syncFreeGiftLineItems();
  }

  /**
   * @returns {string}
   */
  #getSectionIdsParam() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return "";
    const ids = new Set([sectionId]);
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids).join(",");
  }

  /** @param {object} item */
  #isRewardGiftLine(item) {
    const p = item?.properties;
    if (!p || typeof p !== "object" || Array.isArray(p)) return false;
    return String(p[REWARD_GIFT_PROP] || "") === "1";
  }

  /**
   * @param {string} lineItemKey
   * @param {number} quantity
   * @param {string} sectionIdsParam
   * @returns {Promise<boolean>}
   */
  async #changeCartLineByKey(lineItemKey, quantity, sectionIdsParam) {
    const body = JSON.stringify({
      id: String(lineItemKey),
      quantity,
      sections: sectionIdsParam,
      sections_url: window.location.pathname,
    });
    const res = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body })
    );
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.errors) return false;
    const sectionId = this.dataset.sectionId;
    const html = data?.sections?.[sectionId];
    if (html) {
      morphSection(sectionId, html, "hydration", { injectStylesheet: true });
    }
    return true;
  }

  /**
   * @param {number} variantId
   * @param {string} sectionIdsParam
   * @returns {Promise<boolean>}
   */
  async #addCartGiftItem(variantId, sectionIdsParam) {
    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: { [REWARD_GIFT_PROP]: "1" },
        },
      ],
      sections: sectionIdsParam,
      sections_url: window.location.pathname,
    });
    const res = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig("json", { body })
    );
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.errors) return false;
    const sectionId = this.dataset.sectionId;
    const html = data?.sections?.[sectionId];
    if (html) {
      morphSection(sectionId, html, "hydration", { injectStylesheet: true });
    }
    return true;
  }

  async #syncFreeGiftLineItems() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || this.#giftSyncInFlight) return;

    const config = this.#getConfig();
    if (!config) return;

    const giftR = (config.rewards || []).find(
      (r) => r && r.type === "gift" && r.enabled
    );
    if (!giftR || !Number(giftR.thresholdCents)) {
      this.#lastGiftState = "";
      return;
    }

    const subtotalCents = await this.#getCartSubtotalCents(config);
    const eligible = subtotalCents >= Number(giftR.thresholdCents);
    // Demo behaviour: gift ONE product chosen at random from the configured pool,
    // kept stable per cart (hash of the cart cookie) so it doesn't flip on every sync.
    const wantUnique = eligible ? this.#pickGiftVariant(giftR.giftVariantIds) : [];
    const wantSet = new Set(wantUnique);

    const base = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    const cartRes = await fetch(`${base}cart.js`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();
    const items = Array.isArray(cart.items) ? cart.items : [];
    const giftRows = items
      .map((it) => ({
        it,
        vid: Number(it.variant_id),
        key: it.key,
      }))
      .filter((row) => this.#isRewardGiftLine(row.it));

    const kept = new Set();
    const toRemove = [];
    const sorted = [...giftRows].sort((a, b) =>
      String(a.key).localeCompare(String(b.key))
    );
    for (const row of sorted) {
      if (!wantSet.has(row.vid)) {
        toRemove.push(row);
      } else if (kept.has(row.vid)) {
        toRemove.push(row);
      } else {
        kept.add(row.vid);
      }
    }

    const toAdd = wantUnique.filter((id) => !kept.has(id));

    const stateKey = JSON.stringify({
      want: wantUnique.slice().sort((a, b) => a - b),
      have: [...kept].sort((a, b) => a - b),
    });
    if (!toRemove.length && !toAdd.length) {
      this.#lastGiftState = stateKey;
      return;
    }
    if (stateKey === this.#lastGiftState) return;

    const sectionIdsParam = this.#getSectionIdsParam();
    if (!sectionIdsParam) return;

    this.#giftSyncInFlight = true;
    try {
      for (const row of toRemove) {
        if (!row.key) continue;
        await this.#changeCartLineByKey(row.key, 0, sectionIdsParam);
      }
      for (const id of toAdd) {
        await this.#addCartGiftItem(id, sectionIdsParam);
      }
      this.#lastGiftState = stateKey;
    } catch {
      this.#lastGiftState = "";
    } finally {
      this.#giftSyncInFlight = false;
    }
  }

  #buildDiscountState(existingCodes, desiredCodes) {
    const current = [
      ...new Set(
        existingCodes.map((code) => String(code).trim()).filter(Boolean)
      ),
    ].sort();
    const desired = [
      ...new Set(
        desiredCodes.map((code) => String(code).trim()).filter(Boolean)
      ),
    ].sort();
    return JSON.stringify({ current, desired });
  }

  /** @param {object | null} [config] */
  async #getCartSubtotalCents(config) {
    try {
      const base = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
      const res = await fetch(`${base}cart.js`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!res.ok) {
        return Number(config?.cartTotalCents || 0);
      }
      const cart = await res.json();
      const n = Number(cart.items_subtotal_price);
      return Number.isFinite(n) ? n : Number(config?.cartTotalCents || 0);
    } catch {
      return Number(config?.cartTotalCents || 0);
    }
  }

  #computeRewardDiscountCode(config, subtotalCents) {
    const discountRewards = (config?.rewards || []).filter(
      (r) =>
        r &&
        r.type === "discount" &&
        r.enabled &&
        Number(r.thresholdCents) > 0 &&
        String(r.discountCode || "").trim()
    );
    if (!discountRewards.length) {
      return "";
    }
    const active = discountRewards
      .filter((r) => subtotalCents >= Number(r.thresholdCents))
      .sort((a, b) => b.thresholdCents - a.thresholdCents)[0];
    return active?.discountCode ? String(active.discountCode).trim() : "";
  }

  async #syncDiscountReward() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const config = this.#getConfig();
    if (!config) {
      return;
    }

    const subtotalCents = await this.#getCartSubtotalCents(config);
    const targetCode = this.#computeRewardDiscountCode(config, subtotalCents);

    const existingCodes = this.#existingDiscountCodes();
    const rewardDiscountCodes = (config?.rewards || [])
      .filter((reward) => reward.type === "discount")
      .map((reward) => String(reward.discountCode || "").trim())
      .filter(Boolean);
    const nonRewardCodes = existingCodes.filter(
      (code) => !rewardDiscountCodes.includes(String(code).trim())
    );
    const desiredCodes = targetCode
      ? [...nonRewardCodes, targetCode]
      : [...nonRewardCodes];
    const discountState = this.#buildDiscountState(existingCodes, desiredCodes);

    if (discountState === this.#lastDiscountState || this.#syncInFlight) return;

    const uniqueCodes = [...new Set(desiredCodes)].filter(Boolean);

    try {
      this.#syncInFlight = true;
      const response = await fetch(Theme.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: uniqueCodes.join(","),
          sections: [sectionId],
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) {
        this.#lastDiscountState = "";
        return;
      }
      const data = await response.json();
      const html = data?.sections?.[sectionId];
      if (!html) {
        this.#lastDiscountState = "";
        return;
      }
      morphSection(sectionId, html, "full", { injectStylesheet: true });
      this.#lastDiscountState = discountState;
    } catch (error) {
      // Keep cart functional even if reward sync fails.
      this.#lastDiscountState = "";
    } finally {
      this.#syncInFlight = false;
    }
  }

  #bindCheckoutGiftHandler() {
    if (this.#checkoutClickHandler) {
      return;
    }
    this.#checkoutEventTarget =
      this.closest("cart-items-component") || document;
    this.#checkoutClickHandler = (event) => {
      this.#onDelegatedCheckoutClick(event);
    };
    this.#checkoutEventTarget.addEventListener(
      "click",
      this.#checkoutClickHandler,
      true
    );
  }

  /**
   * Checkout is a form submit. If this handler is `async`, the first `await` lets the
   * event finish and the browser navigates to /checkout before we can preventDefault.
   * We must call preventDefault/stopImmediatePropagation synchronously, then do async work.
   */
  #onDelegatedCheckoutClick(event) {
    if (!(event.target instanceof Element)) return;
    const checkoutButton = event.target.closest(
      "button#checkout, button[name='checkout']"
    );
    if (!checkoutButton || !(checkoutButton instanceof HTMLButtonElement)) {
      return;
    }
    const formId = checkoutButton.form?.id;
    if (
      checkoutButton.getAttribute("form") !== "cart-form" &&
      formId !== "cart-form"
    ) {
      return;
    }
    if (checkoutButton.disabled) return;
    if (!this.closest("cart-items-component")?.contains(checkoutButton)) return;
    if (this.#isSubmittingCheckout) return;

    const giftVariantIds = this.#getPendingGiftVariantIds();
    if (!giftVariantIds.length) {
      return;
    }

    // Block native submit / navigation now (sync). Gift add runs after.
    event.preventDefault();
    event.stopImmediatePropagation();

    void this.#addGiftsAndSubmitCheckout(checkoutButton);
  }

  async #addGiftsAndSubmitCheckout(checkoutButton) {
    if (this.#isSubmittingCheckout) return;
    this.#isSubmittingCheckout = true;

    const giftVariantIds = this.#getPendingGiftVariantIds();
    if (!giftVariantIds.length) {
      this.#isSubmittingCheckout = false;
      this.#requestCheckoutSubmit(checkoutButton);
      return;
    }

    const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    const freshVariantIds = await this.#getCartVariantIdsFromServer();
    const stillToAdd = giftVariantIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .filter(
        (id) =>
          !freshVariantIds.map(Number).some((v) => Number(v) === Number(id))
      );

    let submitButton = checkoutButton;
    try {
      for (const variantId of stillToAdd) {
        const res = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { [REWARD_GIFT_PROP]: "1" },
              },
            ],
          }),
        });
        if (!res.ok) {
          return;
        }
        try {
          await res.json();
        } catch {
          // ignore
        }
      }
      if (!submitButton.isConnected) {
        submitButton = this.closest("cart-items-component")?.querySelector(
          "button#checkout, button[name='checkout'][form='cart-form']"
        );
      }
      this.#requestCheckoutSubmit(
        submitButton instanceof HTMLButtonElement
          ? submitButton
          : checkoutButton
      );
    } catch (error) {
    } finally {
      this.#isSubmittingCheckout = false;
    }
  }

  #requestCheckoutSubmit(submitButton) {
    if (!(submitButton instanceof HTMLButtonElement) || !submitButton.form) {
      return;
    }
    if (typeof submitButton.form.requestSubmit === "function") {
      submitButton.form.requestSubmit(submitButton);
    } else {
      submitButton.form.submit();
    }
  }

  /**
   * Gift IDs that must be added at checkout, using `data-rewards-config` (always on host from Liquid + morph)
   * plus `data-pending-gift-variant-ids` (Liquid). Do not rely on `data-unlocked-gift-variant-ids` alone: section
   * morph copies server attributes only and **removes** host-only `data-*` the server didn't send, so
   * JS-written dataset fields disappear right after any cart/section update until `#render` runs.
   * @returns {number[]}
   */
  /**
   * Demo free gift: choose a single variant at random from the configured pool, but keep
   * it stable for a given cart by seeding the choice with the cart cookie. This makes the
   * live cart sync and the checkout-time add agree on the same gift.
   * @param {Array} ids
   * @returns {number[]}
   */
  #pickGiftVariant(ids) {
    const pool = [
      ...new Set(
        (ids || [])
          .map((v) => Number(String(v).trim()))
          .filter((n) => Number.isFinite(n) && n > 0)
      ),
    ];
    if (!pool.length) return [];
    let token = "";
    try {
      const match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      token = match ? match[1] : "";
    } catch {
      token = "";
    }
    let h = 0;
    for (let i = 0; i < token.length; i += 1) {
      h = (h * 31 + token.charCodeAt(i)) >>> 0;
    }
    return [pool[h % pool.length]];
  }

  #getUnlockedGiftVariantIdsFromConfig() {
    const config = this.#getConfig();
    if (!config) return [];
    const subtotal = Number(config.cartTotalCents || 0);
    const raw = (config.rewards || []).find(
      (r) => r && r.type === "gift" && r.enabled
    );
    if (
      !raw ||
      !Number(raw.thresholdCents) ||
      subtotal < Number(raw.thresholdCents)
    ) {
      return [];
    }
    const ids = Array.isArray(raw.giftVariantIds) ? raw.giftVariantIds : [];
    return this.#pickGiftVariant(ids);
  }

  /**
   * @returns {number[]}
   */
  #getPendingGiftVariantIds() {
    const fromConfig = this.#getUnlockedGiftVariantIdsFromConfig();
    if (fromConfig.length) return fromConfig;
    const pending = (this.getAttribute("data-pending-gift-variant-ids") || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (pending.length) {
      return [
        ...new Set(
          pending
            .map((s) => Number(s))
            .filter((n) => Number.isFinite(n) && n > 0)
        ),
      ];
    }
    try {
      const parsed = JSON.parse(this.dataset.unlockedGiftVariantIds || "[]");
      return Array.isArray(parsed)
        ? parsed
            .map((v) => Number(v))
            .filter((n) => Number.isFinite(n) && n > 0)
        : [];
    } catch {
      return [];
    }
  }

  #getCartVariantIdsFromAttr() {
    try {
      return JSON.parse(this.dataset.cartVariantIds || "[]");
    } catch {
      return [];
    }
  }

  /**
   * @returns {Promise<number[]>}
   */
  async #getCartVariantIdsFromServer() {
    const root = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    try {
      const res = await fetch(`${root}cart.js`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!res.ok) return this.#getCartVariantIdsFromAttr();
      const cart = await res.json();
      if (!Array.isArray(cart.items)) return this.#getCartVariantIdsFromAttr();
      return cart.items.map((/** @type {{ variant_id: number }} */ l) =>
        Number(l.variant_id)
      );
    } catch {
      return this.#getCartVariantIdsFromAttr();
    }
  }
}

if (!customElements.get("cart-rewards-component")) {
  customElements.define("cart-rewards-component", CartRewardsComponent);
}
